/**
 * Maps legacy Spanish nationality strings (e.g. "CHILENA", "PERUANO",
 * "CHL", "CHILE") and ISO-3 codes to their canonical ISO-3166-1 alpha-2
 * code (e.g. "CL", "PE"). Returns `null` when the input can't be mapped —
 * common for status descriptors like "EXTRANJERA" or "NACIONALIZADA" that
 * don't identify a country.
 *
 * Kept in sync with the backend copy at
 * `apps/saas_back/src/utils/normalizeNationality.ts` so the EditClientForm
 * Select and the Salesforce push agree on which legacy values resolve to
 * which ISO-2 code.
 */

const collapse = (raw: string): string =>
  raw
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toUpperCase()
    .replace(/[^A-Z]/g, '');

const LEGACY_NATIONALITY_TO_ISO2: Record<string, string> = {
  CHILENA: 'CL',
  CHILENO: 'CL',
  CHL: 'CL',
  CHIL: 'CL',
  CHILE: 'CL',
  VENEZUELA: 'VE',
  VEN: 'VE',
  VENEZOLANA: 'VE',
  VENEZOLANO: 'VE',
  PERU: 'PE',
  PER: 'PE',
  PERUANA: 'PE',
  PERUANO: 'PE',
  BOLIVIA: 'BO',
  BOL: 'BO',
  BOLIVIANA: 'BO',
  BOLIVIANO: 'BO',
  COLOMBIA: 'CO',
  COL: 'CO',
  COLOMBIANA: 'CO',
  COLOMBIANO: 'CO',
  HAITI: 'HT',
  HTI: 'HT',
  HAITIANA: 'HT',
  HAITIANO: 'HT',
  ECUADOR: 'EC',
  ECU: 'EC',
  ECUATORIANA: 'EC',
  ECUATORIANO: 'EC',
  ARGENTINA: 'AR',
  ARG: 'AR',
  ARGENTINO: 'AR',
  REPUBLICADOMINICANA: 'DO',
  DOM: 'DO',
  DOMINICANA: 'DO',
  DOMINICANO: 'DO',
  CUBA: 'CU',
  CUB: 'CU',
  CUBANA: 'CU',
  CUBANO: 'CU',
  PARAGUAY: 'PY',
  PRY: 'PY',
  PARAGUAYA: 'PY',
  PARAGUAYO: 'PY',
  BRASIL: 'BR',
  BRAZIL: 'BR',
  BRA: 'BR',
  BRASILENA: 'BR',
  BRASILENO: 'BR',
  MEXICO: 'MX',
  MEX: 'MX',
  MEXICANA: 'MX',
  MEXICANO: 'MX',
  ELSALVADOR: 'SV',
  SLV: 'SV',
  SALVADORENA: 'SV',
  SALVADORENO: 'SV',
  ITALIA: 'IT',
  ITA: 'IT',
  ITALIANO: 'IT',
  ITALIANA: 'IT',
  URUGUAY: 'UY',
  URY: 'UY',
  URUGUAYO: 'UY',
  URUGUAYA: 'UY',
  ESPANA: 'ES',
  SPAIN: 'ES',
  ESP: 'ES',
  ESPANOL: 'ES',
  ESPANOLA: 'ES',
  FRANCIA: 'FR',
  FRA: 'FR',
  FRANCES: 'FR',
  FRANCESA: 'FR',
  INDIA: 'IN',
  IND: 'IN',
  TURQUIA: 'TR',
  TUR: 'TR',
};

export const normalizeNationalityToIso2 = (raw?: string | null): string | null => {
  if (!raw) return null;
  const collapsed = collapse(raw);
  if (collapsed.length === 0) return null;
  if (collapsed.length === 2) return collapsed;
  return LEGACY_NATIONALITY_TO_ISO2[collapsed] ?? null;
};
